import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { USER_ROLES } from '../config/security'
import { supabase } from '../supabaseClient'
import {
  arquivoParaBase64,
  buscarConversaPorId,
  carregarRelatorioGestor,
  encerrarConversa,
  enviarRespostaCrm,
  formatarTelefoneExibicao,
  formatarTempoResposta,
  isMensagemAudio,
  isMensagemDocumento,
  isMensagemImagem,
  isMensagemVideo,
  listarConversas,
  listarMensagens,
  listarVendedoresEspecialistas,
  marcarConversaLida,
  mensagemFoiEncaminhada,
  salvarContatoConversa,
  textoMensagemWhatsApp,
  textoPreviewConversaWhatsApp,
  TEXTO_MENSAGEM_INICIAL_WHATSAPP,
  WHATSAPP_MAX_ARQUIVO_BYTES
} from '../services/whatsappCrmApi'
import { enviarMensagemWhatsApp } from '../services/whatsappApi'
import {
  adicionarLiberadoProposta,
  listarLiberadosProposta,
  numeroEstaLiberadoProposta,
  removerLiberadoProposta
} from '../utils/whatsappPropostaLiberados'
import WhatsAppAudioPlayer from './WhatsAppAudioPlayer'
import WhatsAppMediaBubble from './WhatsAppMediaBubble'
import { useIntervaloVisivel } from '../hooks/useIntervaloVisivel'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './WhatsAppCrm.css'

const AVATAR_CORES = ['#0d6efd', '#198754', '#6f42c1', '#fd7e14', '#dc3545', '#0dcaf0', '#20c997', '#6c757d']

function iniciaisNome(nome, telefone) {
  const n = String(nome || '').trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    }
    return n.slice(0, 2).toUpperCase()
  }
  const digits = String(telefone || '').replace(/\D/g, '')
  return digits.slice(-2) || '?'
}

function corAvatar(chave) {
  const s = String(chave || '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = s.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_CORES[Math.abs(h) % AVATAR_CORES.length]
}

function mesmasMensagens(a, b) {
  if (a === b) return true
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((m, i) => (
    m.id === b[i].id
    && m.status === b[i].status
    && m.body === b[i].body
    && String(m.payload?.whatsapp_error || '') === String(b[i].payload?.whatsapp_error || '')
  ))
}

function mesmasConversas(a, b) {
  if (a === b) return true
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((c, i) => (
    c.id === b[i].id
    && c.unread_count === b[i].unread_count
    && c.last_message_at === b[i].last_message_at
    && c.last_message_preview === b[i].last_message_preview
    && c.contato_nome === b[i].contato_nome
    && Boolean(c.contato_nome_salvo) === Boolean(b[i].contato_nome_salvo)
  ))
}

function formatarHoraLista(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const agora = new Date()
  if (d.toDateString() === agora.toDateString()) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }
  const ontem = new Date(agora)
  ontem.setDate(agora.getDate() - 1)
  if (d.toDateString() === ontem.toDateString()) return 'Ontem'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function formatarHoraBolha(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const agora = new Date()
  if (d.toDateString() === agora.toDateString()) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const STATUS_MENSAGEM_PT = {
  sent: 'enviado',
  delivered: 'entregue',
  read: 'lido',
  failed: 'falhou',
  pending: 'enviando',
  queued: 'na fila',
  warning: 'alerta',
  deleted: 'excluída',
  played: 'reproduzido',
  accepted: 'aceito'
}

function traduzirStatusMensagem(status) {
  const chave = String(status || '').toLowerCase().trim()
  return STATUS_MENSAGEM_PT[chave] || status
}

function erroFalhaWhatsApp(mensagem) {
  const raw = String(mensagem?.payload?.whatsapp_error || '')
  if (!raw) return ''
  if (raw.includes('131049') || /healthy ecosystem/i.test(raw)) {
    return 'A Meta bloqueou o template de marketing: este contato já recebeu muitas mensagens promocionais. Aguarde pelo menos 24h ou use um template da categoria Utilidade. Não reenvie agora.'
  }
  if (raw.includes('131026')) return 'Número sem WhatsApp ou formato inválido'
  if (raw.includes('131051') || /parameter/i.test(raw)) return 'Variáveis do template inválidas ou ausentes'
  if (raw.includes('130472')) return 'Cliente recusou receber mensagens'
  if (raw.includes('131047')) return 'Janela de 24h expirada'
  if (raw.includes('132001')) return 'Template não encontrado na conta'
  return raw
}

function ultimaFalhaMarketing(mensagens) {
  if (!Array.isArray(mensagens) || !mensagens.length) return false
  for (let i = mensagens.length - 1; i >= 0; i -= 1) {
    const m = mensagens[i]
    if (m.direction !== 'outbound') continue
    if (String(m.status || '').toLowerCase() !== 'failed') return false
    const raw = String(m.payload?.whatsapp_error || '')
    return raw.includes('131049') || /healthy ecosystem/i.test(raw)
  }
  return false
}

const JANELA_24H_MS = 24 * 60 * 60 * 1000
const MSG_JANELA_24H_EXPIRADA =
  'Janela de 24h expirada. A última mensagem do cliente foi há mais de 24 horas. Reabra a conversa enviando um template.'

function pegarUltimaInbound(mensagens) {
  if (!Array.isArray(mensagens) || !mensagens.length) return null
  for (let i = mensagens.length - 1; i >= 0; i -= 1) {
    if (mensagens[i].direction === 'inbound') return mensagens[i].created_at
  }
  return null
}

function janela24hExpiradaAgora(mensagens) {
  const ultima = pegarUltimaInbound(mensagens)
  if (!ultima) return Array.isArray(mensagens) && mensagens.length > 0
  const t = new Date(ultima).getTime()
  if (Number.isNaN(t)) return false
  return Date.now() - t > JANELA_24H_MS
}

function isErroJanela24h(erro) {
  const t = String(erro || '').toLowerCase()
  return t.includes('janela de 24h') || t.includes('24 horas') || t.includes('24h expir')
}

const WhatsAppCrm = ({
  userRole,
  userId,
  chatZap,
  whatsappGestor,
  whatsappProposta,
  conversaInicialId,
  refreshNonce,
  onIniciarConversa,
  modoWidget = false
}) => {
  const isGestor = !modoWidget && Number(whatsappGestor) === 1
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const isEspecialista = userRole === USER_ROLES.ESPECIALISTA
  const chatZapLiberado = Number(chatZap) === 1
  const podeAtender = isEspecialista || chatZapLiberado
  const podeLiberarProposta = Number(whatsappProposta) === 1

  const [vendedores, setVendedores] = useState([])
  const [filtroVendedorId, setFiltroVendedorId] = useState('')
  const [conversas, setConversas] = useState([])
  const [conversaSelecionadaId, setConversaSelecionadaId] = useState(null)
  const [mensagens, setMensagens] = useState([])
  const [relatorio, setRelatorio] = useState([])
  const [textoResposta, setTextoResposta] = useState('')
  const [carregandoConversas, setCarregandoConversas] = useState(false)
  const [carregandoMensagens, setCarregandoMensagens] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState(null)
  const [busca, setBusca] = useState('')
  const [mostrarPainelGestor, setMostrarPainelGestor] = useState(false)
  const chatMessagesRef = useRef(null)
  const mensagensRef = useRef([])
  const conversasRef = useRef([])
  const rolarAoFimRef = useRef(false)
  const fileInputRef = useRef(null)
  const [arquivo, setArquivo] = useState(null)
  const [previewArquivo, setPreviewArquivo] = useState('')
  const [forcarJanelaExpirada, setForcarJanelaExpirada] = useState(false)
  const [mensagemEncaminhar, setMensagemEncaminhar] = useState(null)
  const [buscaEncaminhar, setBuscaEncaminhar] = useState('')
  const [encaminhando, setEncaminhando] = useState(false)
  const [editarContatoAberto, setEditarContatoAberto] = useState(false)
  const [nomeContatoEdit, setNomeContatoEdit] = useState('')
  const [salvandoContato, setSalvandoContato] = useState(false)
  const [iniciarAberto, setIniciarAberto] = useState(false)
  const [telefoneNovo, setTelefoneNovo] = useState('')
  const [iniciandoConversa, setIniciandoConversa] = useState(false)
  const [erroIniciar, setErroIniciar] = useState(null)
  const [liberadosProposta, setLiberadosProposta] = useState([])
  const [salvandoPropostaLib, setSalvandoPropostaLib] = useState(false)

  const conversaSelecionada = conversas.find((c) => c.id === conversaSelecionadaId) || null

  const conversasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return conversas
    return conversas.filter((c) => {
      const nome = String(c.contato_nome || '').toLowerCase()
      const telefone = formatarTelefoneExibicao(c.telefone).toLowerCase()
      const preview = String(c.last_message_preview || '').toLowerCase()
      const vendedor = String(c.vendedor?.nome || '').toLowerCase()
      return nome.includes(termo) || telefone.includes(termo) || preview.includes(termo) || vendedor.includes(termo)
    })
  }, [conversas, busca])

  const conversasParaEncaminhar = useMemo(() => {
    const termo = buscaEncaminhar.trim().toLowerCase()
    return conversas.filter((c) => {
      if (c.id === conversaSelecionadaId) return false
      if (!isGestor && c.vendedor_id !== userId) return false
      if (!termo) return true
      const nome = String(c.contato_nome || '').toLowerCase()
      const telefone = formatarTelefoneExibicao(c.telefone).toLowerCase()
      return nome.includes(termo) || telefone.includes(termo)
    })
  }, [conversas, buscaEncaminhar, conversaSelecionadaId, isGestor, userId])

  const carregarLiberadosPropostaLista = useCallback(async () => {
    if (!podeLiberarProposta) return
    const res = await listarLiberadosProposta()
    if (res.success) setLiberadosProposta(res.itens)
  }, [podeLiberarProposta])

  useEffect(() => {
    carregarLiberadosPropostaLista()
  }, [carregarLiberadosPropostaLista])

  const itemLiberadoAtual = useMemo(() => {
    if (!conversaSelecionada) return null
    return (liberadosProposta || []).find((item) =>
      numeroEstaLiberadoProposta([item], conversaSelecionada.wa_id, conversaSelecionada.telefone)
    ) || null
  }, [liberadosProposta, conversaSelecionada])

  const handleToggleProposta = async () => {
    if (!conversaSelecionada || salvandoPropostaLib) return
    setSalvandoPropostaLib(true)
    setErro(null)
    try {
      if (itemLiberadoAtual) {
        const res = await removerLiberadoProposta(itemLiberadoAtual.id)
        if (!res.success) setErro(res.error)
      } else {
        const res = await adicionarLiberadoProposta({
          telefone: conversaSelecionada.wa_id || conversaSelecionada.telefone,
          nome: conversaSelecionada.contato_nome || ''
        })
        if (!res.success) setErro(res.error)
      }
      await carregarLiberadosPropostaLista()
    } finally {
      setSalvandoPropostaLib(false)
    }
  }

  const carregarVendedores = useCallback(async () => {
    if (!isGestor) return
    const res = await listarVendedoresEspecialistas()
    if (res.success) setVendedores(res.vendedores)
  }, [isGestor])

  const carregarConversas = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCarregandoConversas(true)
    try {
      const res = await listarConversas({
        vendedorId: isGestor ? (filtroVendedorId || null) : userId,
        isGestor
      })
      if (!res.success) {
        if (!silencioso) setErro(res.error)
        return
      }
      let lista = res.conversas || []
      if (conversaInicialId && !lista.some((c) => c.id === conversaInicialId)) {
        const extra = await buscarConversaPorId(conversaInicialId)
        if (extra.success && extra.conversa) {
          lista = [extra.conversa, ...lista]
        }
      }
      setConversas((prev) => (mesmasConversas(prev, lista) ? prev : lista))
    } finally {
      if (!silencioso) setCarregandoConversas(false)
    }
  }, [isGestor, filtroVendedorId, userId, conversaInicialId])

  const carregarRelatorio = useCallback(async () => {
    if (!isGestor) return
    const res = await carregarRelatorioGestor()
    if (res.success) setRelatorio(res.relatorio)
  }, [isGestor])

  const carregarMensagensDaConversa = useCallback(async (conversaId, { silencioso = false } = {}) => {
    if (!conversaId) {
      setMensagens([])
      mensagensRef.current = []
      return
    }
    if (!silencioso) setCarregandoMensagens(true)
    try {
      const res = await listarMensagens(conversaId)
      if (res.success) {
        const novas = res.mensagens || []
        const anteriores = mensagensRef.current
        const mudou = !mesmasMensagens(anteriores, novas)
        if (mudou) {
          const area = chatMessagesRef.current
          const pertoDoFim = !area || (area.scrollHeight - area.scrollTop - area.clientHeight < 96)
          rolarAoFimRef.current = !silencioso || pertoDoFim
          mensagensRef.current = novas
          setMensagens(novas)
        }
        const selecionada = conversasRef.current.find((c) => c.id === conversaId)
        if (Number(selecionada?.unread_count) > 0) {
          await marcarConversaLida(conversaId)
          setConversas((prev) =>
            prev.map((c) => (c.id === conversaId ? { ...c, unread_count: 0 } : c))
          )
        }
      }
    } finally {
      if (!silencioso) setCarregandoMensagens(false)
    }
  }, [])

  useEffect(() => {
    carregarVendedores()
    carregarRelatorio()
  }, [carregarVendedores, carregarRelatorio])

  useEffect(() => {
    let cancelado = false
    ;(async () => {
      await carregarConversas()
      if (!cancelado && conversaInicialId) {
        setConversaSelecionadaId(conversaInicialId)
      }
    })()
    return () => {
      cancelado = true
    }
  }, [carregarConversas, conversaInicialId, refreshNonce])

  useEffect(() => {
    conversasRef.current = conversas
  }, [conversas])

  useEffect(() => {
    mensagensRef.current = mensagens
  }, [mensagens])

  useEffect(() => {
    carregarMensagensDaConversa(conversaSelecionadaId)
    setEditarContatoAberto(false)
  }, [conversaSelecionadaId, carregarMensagensDaConversa])

  useEffect(() => {
    if (!rolarAoFimRef.current) return
    rolarAoFimRef.current = false
    const area = chatMessagesRef.current
    if (area) area.scrollTop = area.scrollHeight
  }, [mensagens])

  const carregarConversasRef = useRef(carregarConversas)
  const carregarMensagensRef = useRef(carregarMensagensDaConversa)
  const carregarRelatorioRef = useRef(carregarRelatorio)
  const conversaSelecionadaIdRef = useRef(conversaSelecionadaId)
  const isGestorRef = useRef(isGestor)
  carregarConversasRef.current = carregarConversas
  carregarMensagensRef.current = carregarMensagensDaConversa
  carregarRelatorioRef.current = carregarRelatorio
  conversaSelecionadaIdRef.current = conversaSelecionadaId
  isGestorRef.current = isGestor

  const sincronizarInbox = useCallback(() => {
    carregarConversasRef.current({ silencioso: true })
    const id = conversaSelecionadaIdRef.current
    if (id) carregarMensagensRef.current(id, { silencioso: true })
    if (isGestorRef.current) carregarRelatorioRef.current()
  }, [])

  useIntervaloVisivel(sincronizarInbox, 5000)

  useEffect(() => {
    if (!userId) return undefined
    const channel = supabase
      .channel(`whatsapp-crm-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'whatsapp_conversas' },
        () => sincronizarInbox()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'whatsapp_mensagens' },
        () => sincronizarInbox()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, sincronizarInbox])

  const handleEnviar = async (e) => {
    e.preventDefault()
    if (!conversaSelecionadaId || !userId) return
    if (!arquivo && !textoResposta.trim()) return

    if (janela24hExpiradaAgora(mensagensRef.current)) {
      setForcarJanelaExpirada(true)
      setErro(null)
      return
    }

    setEnviando(true)
    setErro(null)
    try {
      let res
      if (arquivo) {
        if (arquivo.size > WHATSAPP_MAX_ARQUIVO_BYTES) {
          setErro('Arquivo muito grande. O limite é 3,5 MB.')
          return
        }
        const fileBase64 = await arquivoParaBase64(arquivo)
        res = await enviarRespostaCrm({
          usuarioId: userId,
          conversaId: conversaSelecionadaId,
          type: 'media',
          caption: textoResposta.trim(),
          filename: arquivo.name,
          mimeType: arquivo.type || 'application/octet-stream',
          fileBase64
        })
      } else {
        res = await enviarRespostaCrm({
          usuarioId: userId,
          conversaId: conversaSelecionadaId,
          message: textoResposta.trim()
        })
      }
      if (!res.success) {
        if (isErroJanela24h(res.error)) {
          setForcarJanelaExpirada(true)
          setErro(null)
          return
        }
        setErro(res.error)
        return
      }
      setTextoResposta('')
      limparArquivo()
      await carregarMensagensDaConversa(conversaSelecionadaId)
      await carregarConversas()
      if (isGestor) await carregarRelatorio()
    } finally {
      setEnviando(false)
    }
  }

  const limparArquivo = () => {
    if (previewArquivo) URL.revokeObjectURL(previewArquivo)
    setPreviewArquivo('')
    setArquivo(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const anexarArquivo = async (file, nomeForcado) => {
    if (!file) return
    let anexo = file
    const tipo = String(file.type || '').toLowerCase()
    if (tipo.startsWith('image/') && tipo !== 'image/jpeg' && tipo !== 'image/jpg' && tipo !== 'image/png') {
      try {
        const bitmap = await createImageBitmap(file)
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#fff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(bitmap, 0, 0)
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
        if (!blob) throw new Error('Falha ao converter a imagem.')
        anexo = new File([blob], nomeForcado || 'print.jpg', { type: 'image/jpeg' })
      } catch {
        setErro('Não foi possível anexar este print. Tente salvar como PNG ou JPG.')
        return
      }
    } else if (nomeForcado) {
      anexo = new File([file], nomeForcado, { type: file.type || 'image/png' })
    }

    if (anexo.size > WHATSAPP_MAX_ARQUIVO_BYTES) {
      setErro('Arquivo muito grande. O limite é 3,5 MB.')
      return
    }

    setErro(null)
    if (previewArquivo) URL.revokeObjectURL(previewArquivo)
    if (anexo.type.startsWith('image/')) {
      setPreviewArquivo(URL.createObjectURL(anexo))
    } else {
      setPreviewArquivo('')
    }
    setArquivo(anexo)
  }

  const handleSelecionarArquivo = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) anexarArquivo(file)
  }

  const handleColarMensagem = (e) => {
    const items = Array.from(e.clipboardData?.items || [])
    const imagem = items.find((item) => item.type && item.type.startsWith('image/'))
    if (!imagem) return
    const file = imagem.getAsFile()
    if (!file) return
    e.preventDefault()
    const stamp = new Date()
    const nome = `print-${stamp.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`
    anexarArquivo(file, file.name && file.name !== 'image.png' ? file.name : nome)
  }

  const handleEncaminharPara = async (destinoId) => {
    if (!mensagemEncaminhar || !userId || !destinoId) return
    setEncaminhando(true)
    setErro(null)
    try {
      const res = await enviarRespostaCrm({
        usuarioId: userId,
        conversaId: destinoId,
        type: 'forward',
        mensagemOrigemId: mensagemEncaminhar.id
      })
      if (!res.success) {
        if (isErroJanela24h(res.error)) {
          setMensagemEncaminhar(null)
          setBuscaEncaminhar('')
          setConversaSelecionadaId(destinoId)
          await carregarMensagensDaConversa(destinoId)
          setErro(null)
          return
        }
        setErro(res.error)
        return
      }
      setMensagemEncaminhar(null)
      setBuscaEncaminhar('')
      setConversaSelecionadaId(destinoId)
      await carregarConversas()
      await carregarMensagensDaConversa(destinoId)
    } finally {
      setEncaminhando(false)
    }
  }

  const handleEncerrar = async () => {
    if (!conversaSelecionadaId) return
    if (!window.confirm('Encerrar esta conversa? Novas mensagens do cliente criarão novo atendimento via rodízio.')) return
    const res = await encerrarConversa(conversaSelecionadaId)
    if (res.success) {
      setConversaSelecionadaId(null)
      await carregarConversas()
    } else {
      setErro(res.error)
    }
  }

  const abrirEditarContato = () => {
    if (!conversaSelecionada) return
    setNomeContatoEdit(conversaSelecionada.contato_nome || '')
    setEditarContatoAberto(true)
  }

  const handleSalvarContato = async (e) => {
    e.preventDefault()
    if (!conversaSelecionadaId || !nomeContatoEdit.trim()) return
    setSalvandoContato(true)
    setErro(null)
    try {
      const res = await salvarContatoConversa({
        conversaId: conversaSelecionadaId,
        nome: nomeContatoEdit,
        usuarioId: userId
      })
      if (!res.success) {
        setErro(res.error)
        return
      }
      const nome = res.nome
      setConversas((prev) => prev.map((c) => {
        const mesma = c.id === conversaSelecionadaId
          || (c.idsRelacionados || []).includes(conversaSelecionadaId)
        return mesma ? { ...c, contato_nome: nome, contato_nome_salvo: true } : c
      }))
      setEditarContatoAberto(false)
    } finally {
      setSalvandoContato(false)
    }
  }

  const abrirIniciarConversa = () => {
    setTelefoneNovo('')
    setErroIniciar(null)
    setIniciarAberto(true)
  }

  const handleIniciarConversa = async (e) => {
    e.preventDefault()
    const digits = String(telefoneNovo || '').replace(/\D/g, '')
    if (digits.length < 10) {
      setErroIniciar('Informe um telefone válido (DDD + número).')
      return
    }
    if (!userId) {
      setErroIniciar('Usuário não identificado.')
      return
    }
    setIniciandoConversa(true)
    setErroIniciar(null)
    try {
      const { data, error } = await supabase
        .from('usuarios_sistema')
        .select('whatsapp_template_id, whatsapp_template_nome, whatsapp_template_language')
        .eq('id', userId)
        .maybeSingle()
      if (error || (!data?.whatsapp_template_id && !data?.whatsapp_template_nome)) {
        setErroIniciar(
          'Nenhum template atribuído ao seu usuário. Peça ao administrador para definir em Gerenciamento de Usuários.'
        )
        return
      }
      const res = await enviarMensagemWhatsApp({
        usuario_id: userId,
        to: digits,
        type: 'template',
        template_id: data.whatsapp_template_id || undefined,
        template_name: data.whatsapp_template_nome || '',
        template_language: data.whatsapp_template_language || 'pt_BR',
        body_preview: TEXTO_MENSAGEM_INICIAL_WHATSAPP,
        contato_nome: 'Cliente',
        vendedor_id: userId
      })
      if (!res.success) {
        setErroIniciar(res.error || 'Não foi possível iniciar a conversa.')
        return
      }
      setIniciarAberto(false)
      setTelefoneNovo('')
      await carregarConversas()
      if (res.conversa_id) setConversaSelecionadaId(res.conversa_id)
    } finally {
      setIniciandoConversa(false)
    }
  }

  const ultimaInbound = useMemo(() => pegarUltimaInbound(mensagens), [mensagens])

  const janelaExpirada = janela24hExpiradaAgora(mensagens) || forcarJanelaExpirada

  useEffect(() => {
    setForcarJanelaExpirada(false)
  }, [conversaSelecionadaId])

  useEffect(() => {
    if (!ultimaInbound) return
    if (Date.now() - new Date(ultimaInbound).getTime() <= JANELA_24H_MS) {
      setForcarJanelaExpirada(false)
    }
  }, [ultimaInbound])

  if (!isGestor && !podeAtender) {
    return (
      <div className="wa-crm-denied">
        <p>Acesso ao CRM WhatsApp restrito a usuários liberados no Chat Zap.</p>
      </div>
    )
  }

  const podeResponder = conversaSelecionada
    && (isGestor || conversaSelecionada.vendedor_id === userId)

  const handleReabrirComTemplate = async () => {
    if (!conversaSelecionada || !userId) return
    const numRaw = conversaSelecionada.wa_id || conversaSelecionada.telefone || ''
    const digits = String(numRaw).replace(/\D/g, '')
    if (digits.length < 10) {
      setErro('Telefone da conversa inválido.')
      return
    }
    setEnviando(true)
    setErro(null)
    try {
      const { data, error } = await supabase
        .from('usuarios_sistema')
        .select('whatsapp_template_id, whatsapp_template_nome, whatsapp_template_language')
        .eq('id', userId)
        .maybeSingle()
      if (error || (!data?.whatsapp_template_id && !data?.whatsapp_template_nome)) {
        setErro('Nenhum template atribuído ao seu usuário. Peça ao administrador para definir em Gerenciamento de Usuários.')
        return
      }
      const res = await enviarMensagemWhatsApp({
        usuario_id: userId,
        to: digits,
        type: 'template',
        template_id: data.whatsapp_template_id || undefined,
        template_name: data.whatsapp_template_nome || '',
        template_language: data.whatsapp_template_language || 'pt_BR',
        body_preview: TEXTO_MENSAGEM_INICIAL_WHATSAPP,
        contato_nome: conversaSelecionada.contato_nome || 'Cliente',
        vendedor_id: userId
      })
      if (!res.success) {
        setErro(res.error || 'Não foi possível reabrir a conversa.')
        return
      }
      await carregarConversas()
      await carregarMensagensDaConversa(conversaSelecionadaId)
    } finally {
      setEnviando(false)
    }
  }

  const renderAvatar = (nome, telefone, extraClass = '') => (
    <div
      className={`wa-crm-avatar ${extraClass}`.trim()}
      style={{ backgroundColor: corAvatar(nome || telefone) }}
      aria-hidden
    >
      {iniciaisNome(nome, telefone)}
    </div>
  )

  const classeSidebar = modoWidget
    ? `col-12 sidebar ${conversaSelecionada ? 'd-none' : 'd-flex'}`
    : `col-12 col-lg-4 col-xl-3 sidebar ${conversaSelecionada ? 'd-none d-lg-flex' : 'd-flex'}`
  const classeChat = modoWidget
    ? `col-12 chat-area ${conversaSelecionada ? 'd-flex' : 'd-none'}`
    : `col-12 col-lg-8 col-xl-9 chat-area ${conversaSelecionada ? 'd-flex' : 'd-none d-lg-flex'}`

  return (
    <div className={`container-fluid wa-crm p-0${modoWidget ? ' wa-crm-widget' : ''}`}>
      <div className="row g-0 h-100">
        <div className={classeSidebar}>
          <div className="sidebar-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center min-w-0">
              {renderAvatar(isGestor ? 'Gestor' : 'Atendimento', userId, 'me-2')}
              <span className="fw-semibold text-dark text-truncate">
                {isGestor ? 'Todas as conversas' : 'Minhas conversas'}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-light text-secondary"
                title="Atualizar"
                onClick={carregarConversas}
                disabled={carregandoConversas}
              >
                <i className={`bi ${carregandoConversas ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} />
              </button>
              {isGestor && (
                <button
                  type="button"
                  className={`btn btn-sm btn-light ${mostrarPainelGestor ? 'text-primary' : 'text-secondary'}`}
                  title="Painel do gestor"
                  onClick={() => setMostrarPainelGestor((v) => !v)}
                >
                  <i className="bi bi-gear" />
                </button>
              )}
            </div>
          </div>

          <div className="p-3 bg-white">
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="bi bi-search text-muted" />
              </span>
              <input
                type="text"
                className="form-control bg-light border-start-0 shadow-none"
                placeholder="Buscar conversas..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>

          {isGestor && (
            <div className="px-3 pb-2">
              <select
                className="form-select form-select-sm"
                value={filtroVendedorId}
                onChange={(e) => setFiltroVendedorId(e.target.value)}
              >
                <option value="">Todos os vendedores</option>
                {vendedores.map((v) => (
                  <option key={v.id} value={v.id}>{v.nome}</option>
                ))}
              </select>
            </div>
          )}

          {isGestor && mostrarPainelGestor && (
            <section className="wa-crm-relatorio">
              <h6 className="fw-semibold mb-2">Painel do gestor</h6>
              <div className="table-responsive">
                <table className="table table-sm mb-2">
                  <thead>
                    <tr>
                      <th>Vendedor</th>
                      <th>Conv.</th>
                      <th>Rec.</th>
                      <th>Env.</th>
                      <th>1ª resp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">Nenhum dado ainda.</td></tr>
                    ) : (
                      relatorio.map((r) => (
                        <tr
                          key={r.vendedor_id}
                          className={filtroVendedorId === r.vendedor_id ? 'table-primary' : ''}
                          onClick={() => setFiltroVendedorId(
                            filtroVendedorId === r.vendedor_id ? '' : r.vendedor_id
                          )}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{r.vendedor_nome}</td>
                          <td>{r.conversas_ativas}</td>
                          <td>{r.mensagens_recebidas}</td>
                          <td>{r.mensagens_enviadas}</td>
                          <td>{formatarTempoResposta(r.tempo_resposta_medio_seg)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="wa-crm-hint mb-0">Clique no vendedor para filtrar.</p>
            </section>
          )}

          <div className="contact-list">
            {carregandoConversas && conversas.length === 0 && (
              <p className="text-muted px-3 py-3 mb-0">Carregando...</p>
            )}
            {!carregandoConversas && conversasFiltradas.length === 0 && (
              <p className="text-muted px-3 py-3 mb-0">
                {busca.trim() ? 'Nenhuma conversa encontrada.' : 'Nenhuma conversa ativa.'}
              </p>
            )}
            {conversasFiltradas.map((c) => {
              const nome = c.contato_nome || formatarTelefoneExibicao(c.telefone)
              const temNaoLidas = Number(c.unread_count) > 0
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`contact-item ${conversaSelecionadaId === c.id ? 'active' : ''}`}
                  onClick={() => setConversaSelecionadaId(c.id)}
                >
                  {renderAvatar(c.contato_nome, c.telefone, 'contact-avatar')}
                  <div className="w-100 overflow-hidden text-start">
                    <div className="d-flex justify-content-between">
                      <h6 className="mb-0 fw-semibold text-truncate">{nome}</h6>
                      <small className={temNaoLidas ? 'text-primary fw-semibold' : 'text-muted'}>
                        {formatarHoraLista(c.last_message_at)}
                      </small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted text-truncate d-block pe-2">
                        {textoPreviewConversaWhatsApp(c.last_message_preview)}
                        {isGestor && c.vendedor?.nome ? ` · ${c.vendedor.nome}` : ''}
                      </small>
                      {temNaoLidas && (
                        <span className="badge bg-primary rounded-pill">{c.unread_count}</span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
          <button
            type="button"
            className="wa-crm-nova-conversa"
            title="Nova conversa"
            aria-label="Nova conversa"
            onClick={abrirIniciarConversa}
          >
            <i className="bi bi-plus-lg" />
          </button>
        </div>

        <div className={classeChat}>
          {!conversaSelecionada ? (
            <div className="wa-crm-chat-empty">
              <i className="bi bi-chat-dots text-muted" style={{ fontSize: '2.5rem' }} />
              <p className="mb-0">Selecione uma conversa para visualizar e responder.</p>
              <button type="button" className="btn btn-primary" onClick={abrirIniciarConversa}>
                <i className="bi bi-plus-lg me-1" />
                Nova conversa
              </button>
            </div>
          ) : (
            <>
              <div className="chat-header">
                <button
                  type="button"
                  className={`wa-chat-back ${modoWidget ? '' : 'd-lg-none'}`}
                  onClick={() => setConversaSelecionadaId(null)}
                  title="Voltar às conversas"
                  aria-label="Voltar às conversas"
                >
                  <i className="bi bi-arrow-left" />
                </button>
                {renderAvatar(
                  conversaSelecionada.contato_nome,
                  conversaSelecionada.telefone,
                  'contact-avatar mb-0 me-2'
                )}
                <button
                  type="button"
                  className="wa-chat-identity min-w-0"
                  onClick={abrirEditarContato}
                  title={conversaSelecionada.contato_nome_salvo ? 'Editar contato' : 'Salvar contato'}
                >
                  <h6 className="wa-chat-name">
                    {conversaSelecionada.contato_nome || 'Contato'}
                  </h6>
                  <span className="wa-chat-phone">
                    {formatarTelefoneExibicao(conversaSelecionada.wa_id || conversaSelecionada.telefone)}
                    {isGestor && conversaSelecionada.vendedor?.nome && (
                      <> · {conversaSelecionada.vendedor.nome}</>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  className="wa-chat-contato-btn"
                  title={conversaSelecionada.contato_nome_salvo ? 'Editar contato' : 'Salvar contato'}
                  onClick={abrirEditarContato}
                >
                  <i className={`bi ${conversaSelecionada.contato_nome_salvo ? 'bi-pencil' : 'bi-person-plus'}`} />
                </button>
                {podeLiberarProposta && (
                  <button
                    type="button"
                    className={`wa-chat-proposta ${itemLiberadoAtual ? 'is-on' : ''}`}
                    title={itemLiberadoAtual ? 'Bloquear geração de proposta neste número' : 'Liberar geração de proposta neste número'}
                    onClick={handleToggleProposta}
                    disabled={salvandoPropostaLib}
                  >
                    {itemLiberadoAtual ? 'Proposta on' : 'Liberar proposta'}
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    className="wa-chat-encerrar"
                    title="Encerrar conversa"
                    onClick={handleEncerrar}
                  >
                    Encerrar
                  </button>
                )}
              </div>

              <div className="chat-messages" ref={chatMessagesRef}>
                {carregandoMensagens && mensagens.length === 0 && (
                  <p className="text-muted align-self-center">Carregando mensagens...</p>
                )}
                {mensagens.map((m) => (
                  <div
                    key={m.id}
                    className={`message ${m.direction === 'outbound' ? 'outgoing' : 'incoming'}`}
                  >
                    {mensagemFoiEncaminhada(m) && (
                      <small className="wa-msg-fwd-label">
                        <i className="bi bi-arrow-90deg-right" /> Encaminhada
                      </small>
                    )}
                    {isMensagemAudio(m) ? (
                      <WhatsAppAudioPlayer
                        mensagem={m}
                        userId={userId}
                        outgoing={m.direction === 'outbound'}
                      />
                    ) : isMensagemImagem(m) ? (
                      <WhatsAppMediaBubble mensagem={m} userId={userId} kind="image" />
                    ) : isMensagemVideo(m) ? (
                      <WhatsAppMediaBubble mensagem={m} userId={userId} kind="video" />
                    ) : isMensagemDocumento(m) ? (
                      <WhatsAppMediaBubble mensagem={m} userId={userId} kind="document" />
                    ) : (
                      textoMensagemWhatsApp(m)
                    )}
                    {podeResponder && (
                      <button
                        type="button"
                        className="wa-msg-fwd-btn"
                        title="Encaminhar"
                        onClick={() => {
                          setBuscaEncaminhar('')
                          setMensagemEncaminhar(m)
                        }}
                      >
                        <i className="bi bi-arrow-90deg-right" />
                      </button>
                    )}
                    <span className="message-time">
                      {formatarHoraBolha(m.created_at)}
                      {m.direction === 'outbound' && m.vendedor?.nome ? ` · ${m.vendedor.nome}` : ''}
                      {m.direction === 'outbound' && m.status ? (
                        <span className={`wa-msg-status wa-msg-status-${String(m.status).toLowerCase()}`}>
                          {` · ${traduzirStatusMensagem(m.status)}`}
                          {String(m.status).toLowerCase() === 'failed' && erroFalhaWhatsApp(m)
                            ? ` · ${erroFalhaWhatsApp(m)}`
                            : ''}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>

              {podeResponder && janelaExpirada ? (
                <div className="wa-janela-expirada">
                  <p className="wa-janela-expirada-msg">
                    {ultimaFalhaMarketing(mensagens)
                      ? 'A Meta bloqueou o template de marketing deste contato (limite de mensagens promocionais). Não reenvie agora. Aguarde pelo menos 24 horas ou cadastre um template da categoria Utilidade para reabrir. Mensagens livres só depois que o cliente responder.'
                      : ultimaInbound
                        ? MSG_JANELA_24H_EXPIRADA
                        : 'Janela de 24h expirada. O cliente ainda não enviou mensagem. Reabra a conversa enviando um template.'}
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary wa-janela-expirada-btn"
                    disabled={enviando || ultimaFalhaMarketing(mensagens)}
                    onClick={handleReabrirComTemplate}
                  >
                    {enviando ? 'Enviando...' : ultimaFalhaMarketing(mensagens) ? 'Aguarde 24h para reenviar' : 'Reabrir conversa'}
                  </button>
                </div>
              ) : podeResponder ? (
                <form className="chat-input-area" onSubmit={handleEnviar} onPaste={handleColarMensagem}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="d-none"
                    accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,audio/*,video/mp4,video/3gpp"
                    onChange={handleSelecionarArquivo}
                  />
                  <div className="wa-input-wrap">
                    {arquivo && (
                      <div className="wa-file-chip">
                        {previewArquivo ? (
                          <img src={previewArquivo} alt="" className="wa-file-thumb" />
                        ) : (
                          <i className="bi bi-file-earmark" />
                        )}
                        <span className="text-truncate">{arquivo.name}</span>
                        <button
                          type="button"
                          className="wa-file-chip-close"
                          title="Remover arquivo"
                          onClick={limparArquivo}
                        >
                          <i className="bi bi-x" />
                        </button>
                      </div>
                    )}
                    <div className="wa-composer">
                      <button
                        type="button"
                        className="wa-composer-attach"
                        title="Anexar arquivo"
                        disabled={enviando}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <i className="bi bi-paperclip" />
                      </button>
                      <input
                        type="text"
                        className="wa-composer-input"
                        placeholder={arquivo ? 'Legenda (opcional)...' : 'Digite ou cole um print'}
                        value={textoResposta}
                        onChange={(e) => setTextoResposta(e.target.value)}
                        onPaste={handleColarMensagem}
                        disabled={enviando}
                        maxLength={4096}
                        autoComplete="off"
                      />
                      <button
                        type="submit"
                        className="wa-composer-send"
                        disabled={enviando || (!textoResposta.trim() && !arquivo)}
                        title="Enviar"
                      >
                        <i className="bi bi-send-fill" />
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="wa-crm-readonly">
                  Modo observação — esta conversa pertence a outro vendedor.
                </div>
              )}
            </>
          )}

          {erro && (
            <div className="wa-crm-erro">{erro}</div>
          )}
        </div>
      </div>

      {mensagemEncaminhar && (
        <div className="wa-fwd-overlay" role="dialog" aria-label="Encaminhar mensagem">
          <div className="wa-fwd-modal">
            <div className="wa-fwd-modal-bar">
              <strong>Encaminhar para</strong>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  setMensagemEncaminhar(null)
                  setBuscaEncaminhar('')
                }}
                disabled={encaminhando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="p-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar contato..."
                value={buscaEncaminhar}
                onChange={(e) => setBuscaEncaminhar(e.target.value)}
                disabled={encaminhando}
              />
            </div>
            <div className="wa-fwd-list">
              {conversasParaEncaminhar.length === 0 && (
                <p className="text-muted px-3 py-2 mb-0">Nenhuma conversa disponível.</p>
              )}
              {conversasParaEncaminhar.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="contact-item"
                  disabled={encaminhando}
                  onClick={() => handleEncaminharPara(c.id)}
                >
                  {renderAvatar(c.contato_nome, c.telefone, 'contact-avatar')}
                  <div className="text-start min-w-0">
                    <div className="fw-semibold text-truncate">
                      {c.contato_nome || formatarTelefoneExibicao(c.telefone)}
                    </div>
                    <small className="text-muted text-truncate d-block">
                      {formatarTelefoneExibicao(c.telefone)}
                    </small>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {editarContatoAberto && conversaSelecionada && (
        <div className="wa-fwd-overlay" role="dialog" aria-label="Salvar contato">
          <form className="wa-fwd-modal wa-contato-modal" onSubmit={handleSalvarContato}>
            <div className="wa-fwd-modal-bar">
              <strong>
                {conversaSelecionada.contato_nome_salvo ? 'Editar contato' : 'Salvar contato'}
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setEditarContatoAberto(false)}
                disabled={salvandoContato}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="p-3">
              <p className="text-muted small mb-2">
                {formatarTelefoneExibicao(conversaSelecionada.wa_id || conversaSelecionada.telefone)}
              </p>
              <label className="form-label small mb-1" htmlFor="wa-contato-nome">Nome</label>
              <input
                id="wa-contato-nome"
                type="text"
                className="form-control"
                value={nomeContatoEdit}
                onChange={(e) => setNomeContatoEdit(e.target.value)}
                placeholder="Nome do contato"
                autoFocus
                disabled={salvandoContato}
                maxLength={120}
              />
              <button
                type="submit"
                className="btn btn-primary w-100 mt-3"
                disabled={salvandoContato || !nomeContatoEdit.trim()}
              >
                {salvandoContato ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {iniciarAberto && (
        <div className="wa-fwd-overlay" role="dialog" aria-label="Nova conversa">
          <form className="wa-fwd-modal wa-contato-modal" onSubmit={handleIniciarConversa}>
            <div className="wa-fwd-modal-bar">
              <strong>Nova conversa</strong>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => !iniciandoConversa && setIniciarAberto(false)}
                disabled={iniciandoConversa}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="p-3">
              <p className="text-muted small mb-3">
                Digite o número. A mensagem inicial será enviada automaticamente.
              </p>
              <label className="form-label small mb-1" htmlFor="wa-nova-conversa-tel">Telefone</label>
              <input
                id="wa-nova-conversa-tel"
                type="tel"
                className="form-control"
                value={telefoneNovo}
                onChange={(e) => setTelefoneNovo(e.target.value)}
                placeholder="(11) 99999-9999"
                autoFocus
                disabled={iniciandoConversa}
                autoComplete="tel"
              />
              {erroIniciar && (
                <p className="text-danger small mt-2 mb-0">{erroIniciar}</p>
              )}
              <button
                type="submit"
                className="btn btn-primary w-100 mt-3"
                disabled={iniciandoConversa || String(telefoneNovo).replace(/\D/g, '').length < 10}
              >
                {iniciandoConversa ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </form>
        </div>
      )}

    </div>
  )
}

export default WhatsAppCrm
